import React, { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import formatStringByPattern from 'format-string-by-pattern';
import {
  FormControl,
  FormErrorMessage,
  FormLabel,
  FormHelperText,
  Box,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
} from '@chakra-ui/react';
import { cx } from '@chakra-ui/utils';
import { creditCardType } from '@cloudbeds/credit-card-validator';

import { CardIcon } from './CardIcon';

import type { CreditCardType } from '@cloudbeds/credit-card-validator';
import type { CardInputControlProps } from './types';

const DefaultCardMask = '0000 0000 0000 0000000';
const DefaultCardType = 'default';

const formatCardNumber = (format: string, cardNumber: string) =>
  formatStringByPattern(format, cardNumber.replace(/\D/g, ''));

const detectCard = (
  value: string,
): { type: string; mask: string; valid: boolean } => {
  let mask = DefaultCardMask;
  let type = DefaultCardType;
  let valid = false;

  const cardMatches: CreditCardType[] = creditCardType(
    value.replace(/\D/g, ''),
  );

  if (cardMatches && Array.isArray(cardMatches) && cardMatches.length) {
    const {
      type: cardType,
      gaps,
      lengths,
      skipLuhnCheck = false,
    }: CreditCardType = cardMatches[0];
    valid = creditCardType.isValid(value, !skipLuhnCheck);
    type = cardType;
    mask = creditCardType.mask(gaps, lengths);
  }
  return { type, mask, valid };
};

export const CardInputControl = React.forwardRef<HTMLInputElement, any>(
  (
    {
      name: fieldName,
      label,
      helper,
      error,
      icon = 'right',
      isDisabled,
      isRequired,
      rules,
      ...props
    }: CardInputControlProps,
    forwardRef,
  ) => {
    const { register, trigger, getValues, setValue } = useFormContext();

    const [cardType, setCardType] = useState('default');

    const registerOptions = {
      ...(isRequired && { required: 'This is required' }),
      ...rules,
      validate: {
        valid: (val: any) => {
          const { valid } = detectCard(val);
          return valid || 'Invalid card number';
        },
      },
      onBlur: () => {
        trigger(fieldName);
        if (props.onBlur) props.onBlur(fieldName as unknown as any);
      },
      onChange: () => {
        const val = getValues(fieldName);
        const { type, mask } = detectCard(val);

        setCardType(type || DefaultCardType);
        if (mask) setValue(fieldName, formatCardNumber(mask, val));

        if (props.onChange) props.onChange(fieldName as unknown as any);
      },
    };

    const { ref: inputRef, ...rest } = register(fieldName, {
      ...registerOptions,
    });

    const mergedRef = (e: any) => {
      inputRef(e);
      if (forwardRef) (forwardRef as any).current = e;
    };

    const inputVariant = React.useMemo(
      () => (icon === 'right' ? 'element-right' : 'element-left'),
      [icon],
    );

    return (
      <Box>
        <FormControl
          isInvalid={!!error}
          isDisabled={isDisabled}
          isRequired={isRequired}
          className={cx('InputContainer')}
        >
          <FormLabel className={cx('InputLabel')}>{label}</FormLabel>
          <InputGroup variant={inputVariant}>
            {icon === 'left' && (
              <InputLeftElement
                pointerEvents="none"
                children={<CardIcon icon={cardType} />}
              />
            )}
            <Input
              className={cx('Input')}
              {...props}
              {...(rest as any)}
              ref={mergedRef}
              paddingEnd={8}
            />
            {icon === 'right' && (
              <InputRightElement
                pointerEvents="none"
                children={<CardIcon icon={cardType} />}
              />
            )}
          </InputGroup>

          {helper && (
            <FormHelperText className={cx('InputHelper')}>
              {helper}
            </FormHelperText>
          )}
          {error && (
            <FormErrorMessage className={cx('InputError')}>
              {error as any}
            </FormErrorMessage>
          )}
        </FormControl>
      </Box>
    );
  },
);
